// ============================================================================
// Deleted media accounts (journalist / organisme) — pure row rules.
//
// A deletion is a soft delete (`deleted_at` on media_organizations /
// media_personnel). The league no longer sees the account going forward, but
// past matches keep it (spec 2026-10-24-media-account-deletion): a row whose
// holder is deleted is kept iff its match has been played, whatever the row's
// status (a past rejected request stays in the history and the stats).
//
// "Played" is the exact complement of the deletion RPC's "not yet played"
// (media-accreditation-app migration 20261024120000): matches.status =
// 'completed', OR match_date before today in Africa/Algiers and not
// 'postponed' (a postponed match is upcoming whatever its stale date). A row
// with no match is hidden.
//
// Node-testable: no Supabase client. Pinned by hooks/mediaDeletedLogic.test.ts;
// hooks/mediaDeletedFilters.test.ts guards that the list hooks use it.
// ============================================================================

import { algiersDayKey } from "../utils/algiersTime";

type MaybeDeleted = { deleted_at?: string | null } | null | undefined;

/** The part of a row's match the rule reads (embed `status, match_date`). */
export type MatchPlayedFields =
    | { status?: string | null; match_date?: string | null }
    | null
    | undefined;

/** True when the row carries a deletion timestamp. */
export function isDeleted(row: MaybeDeleted): boolean {
    return !!row?.deleted_at;
}

/** Today as "YYYY-MM-DD" in Algiers wall time. */
export function algiersToday(now: Date = new Date()): string {
    return algiersDayKey(now);
}

/**
 * Has this match been played? 'completed', or dated before `today` (Algiers
 * "YYYY-MM-DD") and not postponed. No match / no date: not played.
 */
export function isMatchPlayed(match: MatchPlayedFields, today: string): boolean {
    if (!match) return false;
    if (match.status === "completed") return true;
    if (match.status === "postponed") return false;
    const day = (match.match_date ?? "").slice(0, 10);
    return day !== "" && today !== "" && day < today;
}

/**
 * Should a request / demand row stay listed? Yes when none of its holders
 * (journalist, organisme) is deleted, or when its match has been played.
 * `today` defaults to the current Algiers day; pass it to evaluate a batch
 * against one day (and in tests).
 */
export function keepRowForDeletedHolder(
    match: MatchPlayedFields,
    holders: MaybeDeleted[],
    today: string = algiersToday()
): boolean {
    if (!holders.some(isDeleted)) return true;
    return isMatchPlayed(match, today);
}
